// src/pages/quality/AssessmentCustomerQualityPage.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import CreateCustomerAssessmentForm from '../../components/quality/CreateCustomerAssessmentForm';
import CustomerAssessmentForm from '../../components/quality/CustomerAssessmentForm';
import CustomerAssessmentCard from '../../components/quality/CustomerAssessmentCard';
import api from '../../services/api';

import {
  useDateTimeFilter,
  toBackendFormat,
  toInputFormat,
  getDefaultDateFrom,
  getDefaultDateTo,
} from '../../hooks/useDateTimeFilter';
import LoadingSpinner from "../../components/common/LoadingSpinner";

const AssessmentCustomerQualityPage = () => {
  const { projects, isAnalyticsView } = useAuth();
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    // Если у пользователя только один проект, выбираем его автоматически
    if (projects && projects.length === 1) {
      return projects[0].id;
    }
    // Иначе пытаемся восстановить из localStorage
    const saved = localStorage.getItem('assessment_customer_project_id');
    return saved ? parseInt(saved, 10) : null;
  });
  const [showForm, setShowForm] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [fieldOptions, setFieldOptions] = useState({});
  const [filters, setFilters] = useState({
    phone_number: '',
    operator_id: '',
    needs_os: '',
    os_given: ''
  });
  
  // Получаем уникальные операторы из оценок
  const uniqueOperators = Array.from(
    new Set(
      assessments
        .map(assessment => assessment.operator_name)
        .filter(operator => operator && operator.trim() !== '')
    )
  ).sort();
  const dateTimeFilter = useDateTimeFilter('', '');
  const {
    filters: dateFilters,
    tempValues: tempDateValues,
    handleChange: handleDateChange,
    handleBlur: handleDateBlur,
  } = dateTimeFilter;
  const navigate = useNavigate();

  useEffect(() => {
    // Авторизация уже проверена через ProtectedRoute
    setLoading(false);
  }, [navigate]);

  // Функция для обновления оценок
  const fetchAssessments = async () => {
    if (!selectedProjectId) return;
    
    setAssessmentsLoading(true);
    try {
      // Формируем URL с фильтрами
      const params = new URLSearchParams({
        project_id: selectedProjectId
      });
      
      // Добавляем фильтры дат в формате %d-%m-%Y %H:%M:%S
      if (dateFilters.date_filter_from) {
        params.append('date_filter_from', toBackendFormat(dateFilters.date_filter_from, 'DD-MM-YYYY HH:mm:ss'));
      }
      if (dateFilters.date_filter_to) {
        params.append('date_filter_to', toBackendFormat(dateFilters.date_filter_to, 'DD-MM-YYYY HH:mm:ss'));
      }
      
      // Добавляем другие фильтры
      if (filters.phone_number) {
        params.append('phone_number', filters.phone_number.replace(/\\D/g, '')); // Удаляем все нецифровые символы
      }
      if (filters.operator_id) {
        params.append('operator_id', filters.operator_id);
      }
      if (filters.needs_os !== '') {
        params.append('needs_os', filters.needs_os);
      }
      if (filters.os_given !== '') {
        params.append('os_given', filters.os_given);
      }

      
      const response = await api.get(`/quality/assessment-customer?${params.toString()}`);
      setAssessments(response.data.data || []);
      
      // Обновляем фильтры дат из ответа сервера, если они отличаются
      if (response.data.filters) {
        const serverFrom = response.data.filters.date_filter_from;
        const serverTo = response.data.filters.date_filter_to;
        
        if (serverFrom && serverFrom !== dateFilters.date_filter_from) {
          handleDateBlur({
            target: { name: 'date_filter_from', value: toInputFormat(serverFrom) }
          });
        }
        
        if (serverTo && serverTo !== dateFilters.date_filter_to) {
          handleDateBlur({
            target: { name: 'date_filter_to', value: toInputFormat(serverTo) }
          });
        }
      }
    } catch (err) {
      console.error('Ошибка загрузки оценок от клиентов:', err);
    } finally {
      setAssessmentsLoading(false);
    }
  };

  // Загрузка оценок при выборе проекта или изменении фильтров
  useEffect(() => {
    if (!selectedProjectId) {
      setAssessments([]);
      return;
    }

    fetchAssessments();
  }, [selectedProjectId, filters, dateFilters.date_filter_from, dateFilters.date_filter_to]);
  
  const handleProjectSelect = (projectId) => {
    setSelectedProjectId(projectId);
    // Сохраняем в localStorage
    if (projectId) {
      localStorage.setItem('assessment_customer_project_id', projectId);
    }
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOpenForm = () => {
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    // Сбрасываем выбранную оценку при закрытии формы
    setSelectedAssessment(null);
    // Не сбрасываем выбранный проект — он сохраняется в localStorage
  };

  const handleAssessmentDeleted = () => {
    // Обновляем список оценок
    fetchAssessments();
  };
  if (loading) {
    return (
        <LoadingSpinner text="Загрузка новостей..." />
      // <div className="loading-container">
      //   <div className="spinner">●●●</div>
      //   <div>Загрузка проектов...</div>
      // </div>
    );
  }

  return (
    <div className="main-content">
      <h1>Прослушка со стороны заказчика</h1>

      {projects.length === 0 ? (
        <div className="no-projects">
          У вас нет доступных проектов.
        </div>
      ) : (
        <div className="form-group">
          <label htmlFor="projectSelect">Выберите проект:</label>
          <select
            id="projectSelect"
            value={selectedProjectId || ''}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            className="project-select"
          >
            <option value="">-- Выберите проект --</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name_text}
              </option>
            ))}
          </select>
        </div>
      )}
      
      {selectedProjectId && (
        <div className="filters-container">
          <div className="date-filters" style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
            <div style={{ flex: 1, minWidth: '250px' }}>
              <label htmlFor="date_filter_from" className="form-label">Дата от</label>
              <input
                type="datetime-local"
                id="date_filter_from"
                name="date_filter_from"
                className="form-control"
                value={tempDateValues.date_filter_from || dateFilters.date_filter_from}
                onChange={handleDateChange}
                onBlur={handleDateBlur}
                step="1"
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: 1, minWidth: '250px' }}>
              <label htmlFor="date_filter_to" className="form-label">Дата до</label>
              <input
                type="datetime-local"
                id="date_filter_to"
                name="date_filter_to"
                className="form-control"
                value={tempDateValues.date_filter_to || dateFilters.date_filter_to}
                onChange={handleDateChange}
                onBlur={handleDateBlur}
                step="1"
                style={{ width: '100%' }}
              />
            </div>
          </div>
          
          <div className="other-filters" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div className="filter-group" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label htmlFor="phone_number_filter">Номер телефона:</label>
                <input
                  type="text"
                  id="phone_number_filter"
                  name="phone_number"
                  value={filters.phone_number}
                  onChange={handleFilterChange}
                  className="filter-input"
                  style={{ width: '100%' }}
                  placeholder="+79991234567"
                />
              </div>
              
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label htmlFor="operator_id_filter">Оператор:</label>
                <select
                  id="operator_id_filter"
                  name="operator_id"
                  value={filters.operator_id}
                  onChange={handleFilterChange}
                  className="filter-select"
                  style={{ width: '100%' }}
                >
                  <option value="">Все</option>
                  <option value="no_operator">Нет оператора</option>
                  {uniqueOperators.map((operator) => (
                    <option key={operator} value={operator}>
                      {operator}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ flex: 1, minWidth: '150px' }}>
                <label htmlFor="needs_os_filter">Требуется ОС:</label>
                <select
                  id="needs_os_filter"
                  name="needs_os"
                  value={filters.needs_os}
                  onChange={handleFilterChange}
                  className="filter-select"
                  style={{ width: '100%' }}
                >
                  <option value="">Все</option>
                  <option value="true">Да</option>
                  <option value="false">Нет</option>
                </select>
              </div>
              
              <div style={{ flex: 1, minWidth: '150px' }}>
                <label htmlFor="os_given_filter">Дана ОС:</label>
                <select
                  id="os_given_filter"
                  name="os_given"
                  value={filters.os_given}
                  onChange={handleFilterChange}
                  className="filter-select"
                  style={{ width: '100%' }}
                >
                  <option value="">Все</option>
                  <option value="true">Да</option>
                  <option value="false">Нет</option>
                </select>
              </div>
              

            </div>
          </div>
        </div>
      )}

      {selectedProjectId && (
        <div className="project-actions" style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-start' }}>
          <button
            className="btn-select-project"
            onClick={handleOpenForm}
          >
            Оценить
          </button>
        </div>
      )}

      {/* Модальное окно формы оценки от клиента */}
      {showForm && selectedProjectId && (
        <div className="modal-overlay">
          <div className="modal-content wide-modal">
            {selectedAssessment ? (
              <CustomerAssessmentForm
                projectId={selectedProjectId}
                onClose={handleCloseForm}
                onAssessmentUpdated={fetchAssessments}
                assessment={selectedAssessment}
              />
            ) : (
              <CreateCustomerAssessmentForm
                projectId={selectedProjectId}
                onClose={handleCloseForm}
                onAssessmentCreated={fetchAssessments}
              />
            )}
          </div>
        </div>
      )}

      {/* Список оценок от клиентов */}
      {selectedProjectId && (
        <div className="assessments-list">
          <h2>История проверок от заказчика</h2>
          {assessmentsLoading ? (
            <div className="loading">Загрузка оценок...</div>
          ) : assessments.length === 0 ? (
            <div className="no-data">Нет оценок для выбранного проекта</div>
          ) : (
            <div className="cards-container">
              {assessments.map((assessment) => (
                <CustomerAssessmentCard
                  key={assessment.id}
                  assessment={assessment}
                  projectSelect={selectedProjectId}
                  onEdit={(assessment) => {
                    setSelectedAssessment(assessment);
                    setShowForm(true);
                  }}
                  onAssessmentDeleted={handleAssessmentDeleted}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssessmentCustomerQualityPage;